import { useGetOrganizationsPaymentPlanPendingChangesQuery } from '@/services/organizations';
import { Box, Button, Card, Popover, Table, useOpenClose } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { OrganizationDocument, PendingOrganizationPaymentPlanChanges, ProgramType } from 'types';

import { useCancelPaymentPlanAction, useResendPendingPaymentPlanEmail } from './PaymentsPending.actions';
import { CancelPaymentProposalModal } from './PaymentsPending.modal';

const Options = ({
  paymentPlanPendingChange,
}: {
  paymentPlanPendingChange: PendingOrganizationPaymentPlanChanges;
}) => {
  const { handleClose, handleToggle, isOpen } = useOpenClose();
  const {
    isOpen: isCancelPaymentProposalModalOpen,
    handleOpen: openCancelPaymentProposalModal,
    handleClose: closeCancelPaymentProposalModal,
  } = useOpenClose();
  const { handleCancelPaymentPlan, isCancelling } = useCancelPaymentPlanAction({
    id: paymentPlanPendingChange.id?.toString(),
  });
  const { handleSendEmail, isSendingEmail } = useResendPendingPaymentPlanEmail({
    id: paymentPlanPendingChange.id?.toString(),
  });
  const allowsCancel = !paymentPlanPendingChange?.proposedPaymentPlanAcceptedBy;
  const allowsEmailResend = !paymentPlanPendingChange?.proposedPaymentPlanAcceptedBy;
  const optionsArray = [];

  if (allowsCancel) {
    optionsArray.push({
      id: 'cancel',
      element: (
        <Button
          size="sm"
          variant="primary"
          tone="neutral"
          onClick={openCancelPaymentProposalModal}
          disabled={isCancelling}
        >
          Cancel
        </Button>
      ),
    });
  }

  if (allowsEmailResend) {
    optionsArray.push({
      id: 'resend',
      element: (
        <Button size="sm" variant="primary" tone="neutral" onClick={handleSendEmail} disabled={isSendingEmail}>
          Resend Email
        </Button>
      ),
    });
  }

  if (!optionsArray.length) {
    return null;
  }

  return (
    <Box>
      <Popover
        trigger="click"
        placement="bottom"
        contentContainerProps={{
          padding: 'sm',
          shadow: 'lg',
          radius: 'sm',
          overflow: 'hidden',
          borderColor: 'separator',
          borderWidth: 'xs',
        }}
        isOpen={isOpen}
        onClickOutside={handleClose}
        content={
          <Box>
            <Box childGap="xs">
              {optionsArray.map((option) => (
                <Box key={option.id} onClick={() => handleClose()}>
                  {option.element}
                </Box>
              ))}
            </Box>
          </Box>
        }
      >
        <Button
          iconPrefix="dots"
          variant="tertiary"
          size="md"
          tone="neutral"
          aria-label="transactionActions"
          onClick={() => handleToggle()}
          style={{
            transform: 'rotate(90deg)',
          }}
        />
      </Popover>

      <CancelPaymentProposalModal
        isModalOpen={isCancelPaymentProposalModalOpen}
        handleCloseModal={closeCancelPaymentProposalModal}
        dataIsUpdating={isCancelling}
        updateFunction={async () => {
          await handleCancelPaymentPlan();
          closeCancelPaymentProposalModal();
        }}
      />
    </Box>
  );
};

export const PendingPaymentsPlan = ({
  organization,
  programType,
}: {
  organization: OrganizationDocument;
  programType: ProgramType;
}) => {
  const {
    data: paymentPlanPendingChange,
    isLoading,
    error,
  } = useGetOrganizationsPaymentPlanPendingChangesQuery(
    {
      programType: programType || ProgramType.solar,
    },
    {
      skip: !organization,
    },
  );

  const [columns, data] = useMemo(() => {
    return [
      [
        {
          heading: 'Payee',
          dataKey: 'organizationName',
        },
        {
          heading: 'Current Plan Name',
          dataKey: 'currentPaymentPlanName',
        },
        {
          heading: 'Proposed Plan Name',
          dataKey: 'proposedPaymentPlanName',
        },
        {
          heading: 'Proposed Effective Date',
          dataKey: 'effectiveDate',
          render: (cell: any) => {
            return <Box>{DateTime.fromISO(cell.toString()).toUTC().toFormat('MM/dd/yyyy')}</Box>;
          },
        },
        {
          heading: 'Created By',
          dataKey: 'createdByName',
        },
        {
          heading: 'Created At',
          dataKey: 'createdAt',
          render: (cell: Date) => {
            return <Box>{cell ? new Date(cell).toLocaleString() : ''}</Box>;
          },
        },
        {
          heading: 'Manage',
          render: (_: any, paymentPlanPendingChange: any) => {
            return (
              <Box fontWeight="medium" maxWidth={'86px'}>
                <Options paymentPlanPendingChange={paymentPlanPendingChange} />
              </Box>
            );
          },
        },
      ],
      paymentPlanPendingChange,
    ];
  }, [paymentPlanPendingChange]);

  const ErrorComponent = error ? (
    <Box padding="md" textAlign="center">
      There was an error loading payment schedule proposals (
      {(error as any)?.data?.message || (error as any)?.message}).
    </Box>
  ) : null;

  return (
    <Card padding="xs" marginTop="0">
      <Card.Header>
        <Box direction="row" justifyContent="space-between" alignItems="center">
          <Box fontSize="xl" fontWeight="medium">
            Payment Plan Proposals
          </Box>
        </Box>
      </Card.Header>
      <Box margin="0 lg">
        {Array.isArray(paymentPlanPendingChange) && paymentPlanPendingChange.length === 0 ? (
          <Box padding="md" textAlign="center">
            No payment schedule proposals found.
          </Box>
        ) : error ? (
          ErrorComponent
        ) : (
          <Table
            columns={columns as any}
            rows={data || []}
            rowKey="createdAt"
            isLoading={isLoading}
            align="left"
            isScrollable={{
              x: true,
              y: false,
            }}
          />
        )}
      </Box>
    </Card>
  );
};
